import { useQuery } from '@tanstack/react-query'
import { FileText, ExternalLink, Loader2, AlertCircle } from 'lucide-react'
import api from '@/lib/api'
import { accessToken } from '@/lib/authToken'
import { useEffect, useState } from 'react'

type Identifier = { type: string; value: string }

type FactsheetSummary = Record<string, string | undefined>

type Identity = {
  fund_id: string
  source: string
  ticker: string | null
  name: string
  short_name: string | null
  asset_type: string | null
  currency: string | null
  country: string | null
  exchange: string | null
  sector: string | null
  industry: string | null
  description: string | null
  inception_date: string | null
  fund: {
    share_class?: string | null
    management_fee?: number | null
    performance_fee?: number | null
    benchmark?: string | null
    hurdle_rate?: number | null
    min_subscription?: number | null
    mandate?: string | null
  }
  identifiers: Identifier[]
  factsheet: {
    available: boolean
    source?: string
    source_url?: string
    as_of?: string
    web_summary?: FactsheetSummary
    references?: { label: string; url: string }[]
  }
}

const PRETTY_KEY: Record<string, string> = {
  manager: 'Manager',
  launch_date: 'Launch',
  share_class: 'Share Class',
  isin: 'ISIN',
  sfdr_article: 'SFDR',
  domicile: 'Domicile',
  structure: 'Structure',
  target_return: 'Target Return',
  historical_vol_target: 'Historical Vol',
  correlation_msci_world: 'Corr. MSCI World',
  portfolio_breadth: 'Breadth',
  liquidity: 'Liquidity',
  management_fee: 'Mgmt Fee',
  performance_fee: 'Perf Fee',
  benchmark: 'Benchmark',
  style: 'Style',
  intent: 'Intent',
  notes: 'Notes',
}

function pct(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—'
  return `${(v * 100).toFixed(2)}%`
}

export default function FundIdentityCard({ fundId }: { fundId: string }) {
  const [pdfObjectUrl, setPdfObjectUrl] = useState<string | null>(null)
  const [pdfError, setPdfError] = useState<string | null>(null)
  const [pdfLoading, setPdfLoading] = useState(false)

  const q = useQuery<Identity>({
    queryKey: ['fund-identity', fundId],
    queryFn: () => api.get(`/fund-analysis/identity/${fundId}`).then(r => r.data),
    enabled: !!fundId,
  })

  const identity = q.data
  const hasFactsheet = !!identity?.factsheet?.available

  async function openFactsheet() {
    if (!fundId || !hasFactsheet) return
    setPdfError(null)
    setPdfLoading(true)
    try {
      // Raw fetch rather than the axios client because the response is a PDF blob,
      // so the token has to be fetched the same way the interceptor would.
      const token = await accessToken()
      const base = (import.meta.env.VITE_API_URL as string) || '/api'
      const resp = await fetch(`${base}/fund-analysis/factsheet/${fundId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      const blob = await resp.blob()
      const url = URL.createObjectURL(blob)
      setPdfObjectUrl(url)
      window.open(url, '_blank', 'noopener')
    } catch (e: any) {
      setPdfError(e?.message || 'Failed to load factsheet')
    } finally {
      setPdfLoading(false)
    }
  }

  useEffect(() => () => {
    if (pdfObjectUrl) URL.revokeObjectURL(pdfObjectUrl)
  }, [pdfObjectUrl])

  if (!fundId) return null

  if (q.isLoading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 flex items-center gap-3">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm text-muted-foreground">Loading fund identity…</span>
      </div>
    )
  }

  if (q.isError || !identity) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 flex items-center gap-3">
        <AlertCircle className="h-4 w-4 text-destructive" />
        <span className="text-sm text-muted-foreground">Fund identity unavailable.</span>
      </div>
    )
  }

  const web = identity.factsheet?.web_summary || {}
  const summaryEntries = Object.entries(web).filter(([, v]) => v) as [string, string][]

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl font-bold truncate">{identity.name}</h2>
            {identity.ticker && (
              <span className="inline-flex items-center px-2 py-0.5 text-xs rounded bg-muted text-muted-foreground font-mono">
                {identity.ticker}
              </span>
            )}
            {identity.currency && (
              <span className="inline-flex items-center px-2 py-0.5 text-xs rounded bg-muted text-muted-foreground">
                {identity.currency}
              </span>
            )}
            {identity.source && (
              <span className="inline-flex items-center px-2 py-0.5 text-xs rounded bg-primary/10 text-primary uppercase">
                {identity.source}
              </span>
            )}
          </div>
          {identity.fund?.mandate && (
            <div className="text-sm text-muted-foreground mt-1">
              Mandate: <span className="text-foreground">{identity.fund.mandate}</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {hasFactsheet ? (
            <button
              type="button"
              onClick={openFactsheet}
              disabled={pdfLoading}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded border border-border hover:bg-muted transition-colors"
            >
              {pdfLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileText className="h-4 w-4" />
              )}
              Factsheet
              {identity.factsheet.as_of && (
                <span className="text-xs text-muted-foreground">({identity.factsheet.as_of})</span>
              )}
            </button>
          ) : (
            <span className="text-xs text-muted-foreground">No factsheet on file</span>
          )}
          {identity.factsheet?.source_url && (
            <a
              href={identity.factsheet.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded border border-border hover:bg-muted transition-colors"
            >
              {identity.factsheet.source || 'Source'}
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </div>

      {pdfError && (
        <div className="mt-2 text-xs text-destructive">{pdfError}</div>
      )}

      {/* Static metadata grid (DB) */}
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        {identity.fund?.share_class && (
          <Field label="Share Class" value={identity.fund.share_class} />
        )}
        {identity.inception_date && (
          <Field label="Inception" value={identity.inception_date} />
        )}
        {identity.fund?.management_fee != null && (
          <Field label="Mgmt Fee" value={pct(identity.fund.management_fee)} />
        )}
        {identity.fund?.performance_fee != null && (
          <Field label="Perf Fee" value={pct(identity.fund.performance_fee)} />
        )}
        {identity.fund?.benchmark && (
          <Field label="Benchmark" value={identity.fund.benchmark} />
        )}
        {identity.country && <Field label="Country" value={identity.country} />}
        {identity.exchange && <Field label="Exchange" value={identity.exchange} />}
        {identity.asset_type && (
          <Field label="Asset Type" value={identity.asset_type} />
        )}
      </div>

      {/* Identifiers */}
      {identity.identifiers?.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {identity.identifiers.map((id) => (
            <span
              key={`${id.type}:${id.value}`}
              className="inline-flex items-center px-2 py-0.5 text-xs rounded bg-muted font-mono"
              title={id.type}
            >
              <span className="text-muted-foreground mr-1">{id.type}</span>
              {id.value}
            </span>
          ))}
        </div>
      )}

      {/* Web-scraped summary */}
      {summaryEntries.length > 0 && (
        <div className="mt-5 border-t border-border pt-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
            Factsheet summary · {identity.factsheet.source || 'web'}
            {identity.factsheet.as_of && ` · as of ${identity.factsheet.as_of}`}
          </div>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {summaryEntries.map(([k, v]) => (
              <div key={k} className="flex gap-2">
                <dt className="text-muted-foreground min-w-[140px]">
                  {PRETTY_KEY[k] ?? k}
                </dt>
                <dd className="text-foreground">{v}</dd>
              </div>
            ))}
          </dl>

          {identity.factsheet?.references && identity.factsheet.references.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-3 text-xs">
              {identity.factsheet.references.map((r) => (
                <a
                  key={r.url}
                  href={r.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary hover:underline"
                >
                  {r.label}
                  <ExternalLink className="h-3 w-3" />
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  )
}
